import React, { useEffect, useState } from 'react';
import * as AppPics from 'assets/images';
import { AppTextField, AppUploadBlock, AppRadioBox, AppButton, AppSwitchBox } from 'components';
import Joi from 'joi';
import usePodcastCategory from 'hooks/API/POST/usePodcastCategory';
import { checkForErrors } from 'utilities/fns';
import { useHistory, useParams, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import useGetOneCategory from 'hooks/API/GET/useGetOneCategory';
import { notifier } from 'components/Toaster';
import { getRandomUid } from 'utilities/uuid.service';
import { useQueryClient } from 'react-query';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';

const actionsConfig = {
  open: false,
  data: {},
  Header: null,
  Body: null,
  isDeleteOperation: false,
};

function ManageCategory() {
  const { categoryId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();

  const params = new URLSearchParams(location.search);
  const redirectBack = params.get('redirect_back');

  const [category, setCategory] = useState('');
  const [url, setUrl] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [categoryImage, setCategoryImage] = useState(null);
  const [categoryImageFullPath, setCategoryImageFullPath] = useState(null);
  const [categoryImageFullPathUploadData, setCategoryImageUploadData] = useState('');
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [referenceKey, setReferenceKey] = useState('');
  const [formErrors, setFormErrors] = useState([]);
  const [validationErrorServer, setValidationErrorServer] = useState({});
  const [deletedImage, setDeletedImage] = useState(false);
  const [actions, setActions] = useState({ ...actionsConfig });
  const [published, setPublished] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [genreId, setGenreId] = useState('');

  const { mutate, isLoading, isError, isSuccess, data, error } = usePodcastCategory();

  const clearForm = () => {
    setCategory('');
    setUrl('');
    setCategoryImage(null);
    setSelectedType('');
    setCategoryImageFullPath(null);
    setCategoryImageUploadData(null);
    setFormErrors([]);
    setValidationErrorServer({});
    setDisableSubmit(false);
    setDeletedImage(false);
    setReferenceKey(getRandomUid());
    setPublished(null);
  };

  const handleGoBack = () => {
    if (redirectBack) {
      history.replace(decodeURIComponent(redirectBack));
    } else {
      history.goBack();
    }
  };

  useEffect(() => {
    if (categoryId === 'add') {
      clearForm();
      queryClient.removeQueries(['getSingleCategory', categoryId]);
      return;
    }
  }, [categoryId, queryClient]);

  const getSingleCategory = useGetOneCategory({
    enabled: categoryId !== 'add',
    categoryId,
    config: { params: { categoryId } },
  });

  useEffect(() => {
    if (categoryId !== 'add' && getSingleCategory?.data && getSingleCategory?.isSuccess) {
      const currentData = getSingleCategory.data?.data?.data || getSingleCategory.data?.data;

      setCategory(currentData.categoryName || '');
      setUrl(currentData.categoryUrl || '');
      setCategoryImage(currentData.categoryImage || null);
      setCategoryImageFullPath(currentData.categoryImg || null);
      setSelectedType(currentData.categorySection || '');
      setReferenceKey(currentData.referenceKey || getRandomUid());
      setDeletedImage(false);
      setPublished(currentData.isPublished || null);
      setIsSaved(true);
    }
  }, [categoryId, getSingleCategory?.data, getSingleCategory?.isSuccess]);

  const handleCategoryChange = (event) => {
    const newValue = event.target.value;
    setCategory(newValue);
  };

  const handleImageChange = ({ file, uploadedData }) => {
    setCategoryImageUploadData(uploadedData);
    setCategoryImageFullPath(`${uploadedData.basePath}${uploadedData.file}`);
    setCategoryImage(uploadedData.file);
    setDeletedImage(false);
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
  };

  function handleSave() {
    setFormErrors([]);
    if (disableSubmit) return;

    const data = {
      name: category.trim(),
      url: url.trim(),
      image: deletedImage ? null : categoryImage ?? null,
      section: selectedType,
      canPublish: published,
      ...(categoryId && categoryId !== 'add' ? { categoryId } : {}),
    };
    if (deletedImage) {
      data.imageDeleted = true;
    }

    const validation = categorySchema.validate(data, { abortEarly: false, allowUnknown: true });
    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    mutate(data, {
      onError(failData) {
        const errorData = failData?.response?.data;
        const errorMessage = errorData?.message;
        const validationErrors = errorData?.errors;

        setValidationErrorServer(validationErrors || {});
        notifier.error(errorMessage);
      },

      onSuccess(response) {
        const successMessage = response?.data?.data?.message || response?.data?.message;
        notifier.success(successMessage);
        // handleGoBack();
        setGenreId(response?.data?.data?.record);
        setIsSaved(true);

        queryClient.invalidateQueries(['categories']);
        queryClient.invalidateQueries(['getSingleCategory']);
      },
    });
  }

  const handlePublish = (value) => {
    setFormErrors([]);
    if (disableSubmit) return;

    const data = {
      name: category.trim(),
      url: url.trim(),
      image: deletedImage ? null : categoryImage ?? null,
      section: selectedType,
      canPublish: value,
      categoryId: categoryId && categoryId !== 'add' ? categoryId : genreId,
    };

    mutate(data, {
      onError(failData) {
        const errorData = failData?.response?.data;
        const errorMessage = errorData?.message;
        const validationErrors = errorData?.errors;

        setValidationErrorServer(validationErrors || {});
        notifier.error(errorMessage);
      },

      onSuccess() {
        notifier.success(`Genre ${value ? 'published' : 'unpublished'} successfully`);
        setTimeout(() => {
          handleGoBack();
        }, 2000);
        queryClient.invalidateQueries(['categories']);
        queryClient.invalidateQueries(['getSingleCategory']);
      },
    });
  };

  const errors = checkForErrors(formErrors);

  const handleRadioKeyDown = (e, value) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTypeChange(value);
    }
  };

  const handleDeleteCategoryImage = () => {
    setCategoryImage(null);
    setCategoryImageFullPath(null);
    setCategoryImageUploadData(null);
    setDeletedImage(true);
    notifier.success('Image deleted successfully');
  };

  const handleDeleteCategoryImageConfirm = () => {
    setActions({
      open: true,
      Header: 'Delete Image',
      Body: 'Are you sure you want to delete this image?',
      isDeleteOperation: true,
      data: {
        type: 'CATEGORY_IMAGE',
      },
    });
  };

  const handlePublishToggleConfirm = (newValue) => {
    setActions({
      open: true,
      Header: 'Confirm Publish Status',
      Body: `Are you sure you want to ${newValue ? 'publish' : 'unpublish'} this Genre?`,
      isDeleteOperation: false,
      data: {
        type: 'PUBLISH_TOGGLE',
        value: newValue,
      },
    });
  };

  const handleActionsConfirm = () => {
    const { type, value } = actions.data || {};
    if (type === 'CATEGORY_IMAGE') {
      handleDeleteCategoryImage();
    }
    if (type === 'PUBLISH_TOGGLE') {
      handlePublish(value);
    }
    setActions({ ...actionsConfig });
  };

  return (
    <>
      <BasicDelete
        open={actions.open}
        closeModal={() => setActions({ ...actionsConfig })}
        Header={actions.Header}
        Body={actions.Body}
        handleConfirmation={handleActionsConfirm}
      />
      <div className="mainWraper coursetabsection">
        <div className="tab">
          <ul className="tab__menu">
            <li className="back">
              <button className="btn-back" onClick={handleGoBack}>
                <img src={AppPics.BackSvg} alt="Back" />
              </button>
            </li>
          </ul>

          <div
            className="tab__content
          ___adjust___tab___padding"
          >
            <div className="tab__content-actHead">
              <AppBreadcrumbs
                links={[
                  { label: 'Dashboard', to: '/dashboard' },
                  { label: 'Podcast Home', to: '/podcast' },
                  { label: 'Genres', to: '/podcast/category' },
                ]}
                current={categoryId === 'add' ? 'Add Genre' : 'Update Genre'}
              />
            </div>
            <div className="tab__content-actBody">
              <div className="px-5 py-5">
                <div className="category-form">
                  <div className="row mt-4">
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      {categoryId !== 'add' ? (
                        <h2 className="prime-head p-0 boldtitle">Update Genre</h2>
                      ) : (
                        <h2 className="prime-head p-0 boldtitle">Add Genre</h2>
                      )}
                      {validationErrorServer && (
                        <ul className="error-list">
                          {Object.values(validationErrorServer).map((err, idx) => (
                            <li key={idx}>{err.msg}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className="row mt-4">
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <AppTextField
                        labelProps={{ label: 'Podcast Genre *', htmlFor: 'podcast-genre' }}
                        inputProps={{
                          placeholder: 'Podcast Genre',
                          id: 'categoryName',
                          name: 'categoryName',
                          value: category,
                          onChange: handleCategoryChange,
                          tabIndex: 1,
                        }}
                        fieldErrors={errors.name}
                      />
                      {errors.name && <div className="text-danger">{errors.name.errorMessage}</div>}
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12">
                      <AppTextField
                        labelProps={{
                          label: 'URL',
                          htmlFor: 'url',
                        }}
                        inputProps={{
                          placeholder: 'url',
                          id: 'url',
                          name: 'url',
                          value: url,
                          onChange: (event) => setUrl(event.target.value),
                          tabIndex: 2,
                        }}
                        fieldErrors={errors['url']}
                      />
                      {errors.url && <div className="text-danger">{errors.url.errorMessage}</div>}
                    </div>
                  </div>

                  <div className="row mt-4">
                    <div className="col-lg-4 col-md-6 col-sm-12">
                      <AppUploadBlock
                        labelProps={{ label: 'Genre Image' }}
                        props={{
                          isImage: true,
                          isPublic: true,
                          referenceKey: 'PODCAST_CATEGORY',
                          uploadCallback: (value) => {
                            setDisableSubmit(value);
                          },
                          handleChange: (data = {}) => {
                            const { file, uploadedData } = data;
                            handleImageChange({ file, uploadedData });
                            setDeletedImage(false);
                          },
                          fileData: {
                            fullPath: deletedImage ? null : categoryImageFullPath,
                          },
                          clearFile: deletedImage,
                          tabIndex: 3,
                        }}
                      />
                      {(categoryImage || categoryImageFullPath) && !deletedImage && (
                        <button
                          className="actions-icons mx-2 text-danger mt-2"
                          title="Delete Genre Image"
                          onClick={handleDeleteCategoryImageConfirm}
                          type="button"
                        >
                          <img src={AppPics.DeleteSvg} alt="Delete" />
                        </button>
                      )}
                    </div>
                    <div className="col-lg-8 col-md-6 col-sm-12">
                      <label className="label d-block">
                        Podcast section
                        <span>
                          <small className="text-muted">
                            {' '}
                            (Popular Genres appear in the Popular Genres section on the Dashboard
                            page. Trending is currently not in use.)
                          </small>
                        </span>
                      </label>
                      <div>
                        <AppRadioBox
                          inputProps={{
                            name: 'section',
                            value: 'trending',
                            checked: selectedType === 'trending',
                            onChange: (e) => handleTypeChange(e.target.value),
                            onFocus: () => handleTypeChange('trending'),
                            onKeyDown: (e) => handleRadioKeyDown(e, 'trending'),
                            tabIndex: 4,
                          }}
                          labelProps={{ label: 'Trending' }}
                        />
                        <AppRadioBox
                          inputProps={{
                            name: 'section',
                            value: 'popular',
                            checked: selectedType === 'popular',
                            onChange: (e) => handleTypeChange(e.target.value),
                            onFocus: () => handleTypeChange('popular'),
                            onKeyDown: (e) => handleRadioKeyDown(e, 'popular'),
                            tabIndex: 5,
                          }}
                          labelProps={{ label: 'Popular' }}
                        />
                      </div>
                      {errors.section && (
                        <div className="text-danger">{errors.section.errorMessage}</div>
                      )}
                    </div>
                  </div>

                  <div className="d-flex justify-content-end">
                    <AppButton
                      type="submit"
                      variantClass="btn-primary mx-3"
                      style={{ minWidth: '1.5rem' }}
                      onClick={() => handlePublishToggleConfirm(!published)}
                      disabled={!isSaved || isLoading}
                      tabIndex={6}
                    >
                      {published ? 'Unpublish Genre' : 'Publish Genre'}
                    </AppButton>
                    <AppButton
                      type="submit"
                      variantClass="btn-primary mx-3"
                      style={{ minWidth: '1.5rem' }}
                      onClick={handleSave}
                      disabled={disableSubmit || isLoading}
                      tabIndex={6}
                    >
                      {isLoading ? 'Saving...' : 'Save Genre'}
                    </AppButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const categorySchema = Joi.object({
  name: Joi.string().min(1).max(100).required().messages({
    'string.base': 'Category name must be a string.',
    'string.empty': 'Category name cannot be empty.',
    'string.max': 'Category name must be at most 100 characters long.',
    'any.required': 'Category name is required.',
  }),
});

export default ManageCategory;
